import type { ListBuilderConfiguration } from '../types';

/**
 * Per-client trip-stop history list. Embedded inside the Distribution
 * tab on `/clients/:id`. Columns mirror the data shape returned by
 * `GET /api/clients/:id/trip-history` (see
 * `ClientDistributionService.getClientTripHistory`):
 *
 *   - **Trip date** — sortable; default DESC so most-recent visits land
 *     at the top.
 *   - **Route** — text from the joined `route.name`.
 *   - **Arrival / departure** — captured by the driver's check-in/out;
 *     null until the stop completes.
 *   - **Planned (min)** — effective stop duration via the cascade
 *     (trip_stop → route_client → client → tenant default). Computed
 *     server-side and serialized as a plain number per row.
 *   - **Actual (min)** — `departure - arrival` in minutes. Null when
 *     the stop never closed.
 *   - **Variance** — actual minus planned, in minutes. Surfaces
 *     stops that ran longer than budget — useful when reviewing the
 *     configured per-client stop time.
 *   - **Status** — the trip-stop status (completed / skipped / etc.).
 */
export const CLIENT_TRIP_HISTORY_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-client-trip-history',
			cacheTTL: 60000,
			name: 'Trip History',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'tripDate',
					headers: [{ id: 'tripDate-header', text: 'Trip Date' }],
					fields: [
						{
							id: 'tripDate-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'tripDate',
									objectTable: 'client_trip_history',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
								formatOptions: { dateFormat: 'date-medium' },
							},
							displayStyle: 'date',
						},
					],
					sortable: true,
					width: 130,
				},
				{
					id: 'routeName',
					headers: [{ id: 'routeName-header', text: 'Route' }],
					fields: [
						{
							id: 'routeName-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'routeName',
									objectTable: 'client_trip_history',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
				},
				{
					id: 'arrivalTime',
					headers: [{ id: 'arrivalTime-header', text: 'Arrived' }],
					fields: [
						{
							id: 'arrivalTime-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'arrivalTime',
									objectTable: 'client_trip_history',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
								formatOptions: { dateFormat: 'datetime-short' },
							},
							displayStyle: 'date',
						},
					],
					sortable: true,
					width: 100,
				},
				{
					id: 'departureTime',
					headers: [{ id: 'departureTime-header', text: 'Departed' }],
					fields: [
						{
							id: 'departureTime-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'departureTime',
									objectTable: 'client_trip_history',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
								formatOptions: { dateFormat: 'datetime-short' },
							},
							displayStyle: 'date',
						},
					],
					sortable: true,
					width: 100,
				},
				{
					id: 'plannedDurationMinutes',
					headers: [{ id: 'planned-header', text: 'Planned (min)' }],
					fields: [
						{
							id: 'planned-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'plannedDurationMinutes',
									objectTable: 'client_trip_history',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 110,
					align: 'right',
				},
				{
					id: 'actualDurationMinutes',
					headers: [{ id: 'actual-header', text: 'Actual (min)' }],
					fields: [
						{
							id: 'actual-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'actualDurationMinutes',
									objectTable: 'client_trip_history',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					width: 110,
					align: 'right',
				},
				{
					id: 'varianceMinutes',
					headers: [{ id: 'variance-header', text: 'Variance' }],
					fields: [
						{
							id: 'variance-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'varianceMinutes',
									objectTable: 'client_trip_history',
								},
							},
							styleConfig: {
								size: 'sm',
								// Negative variance (faster than planned) is fine —
								// no special highlight. Positive variance >5 min
								// is what we want to draw attention to.
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 100,
					align: 'right',
				},
				{
					id: 'status',
					headers: [{ id: 'status-header', text: 'Status' }],
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'status',
									objectTable: 'client_trip_history',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					width: 110,
				},
			],
			defaultSortId: 'trip-date-desc',
			sortOrder: 1, // DESC — most-recent visits at the top
		},
	],
	paging: {
		defaultPageSize: 10,
		pageSizeOptions: [10, 20, 50],
	},
	filters: {
		// No tenant-wide filter chips here yet — the client context
		// alone scopes the result set tightly enough. Date range and
		// status filters can be added in a follow-up once we see what
		// dispatchers actually want to slice by.
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			objectLabel: 'Client Trip History',
			objectTable: 'client_trip_history',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'tripStopId', fieldLabel: 'Trip Stop ID' },
				{ fieldName: 'tripId', fieldLabel: 'Trip ID' },
				{ fieldName: 'tripDate', fieldLabel: 'Trip Date' },
				{ fieldName: 'routeId', fieldLabel: 'Route ID' },
				{ fieldName: 'routeName', fieldLabel: 'Route' },
				{ fieldName: 'sequence', fieldLabel: 'Sequence' },
				{ fieldName: 'arrivalTime', fieldLabel: 'Arrived' },
				{ fieldName: 'departureTime', fieldLabel: 'Departed' },
				{
					fieldName: 'plannedDurationMinutes',
					fieldLabel: 'Planned (min)',
				},
				{
					fieldName: 'actualDurationMinutes',
					fieldLabel: 'Actual (min)',
				},
				{ fieldName: 'varianceMinutes', fieldLabel: 'Variance' },
				{ fieldName: 'status', fieldLabel: 'Status' },
			],
		},
	],
	// PD-15683 — same shape rule as client_visits: the default sort must be a
	// root-level TableSortOption, not a column id.
	tableSorts: [
		{
			id: 'trip-date-desc',
			name: 'Trip Date (Newest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'tripDate',
					objectTable: 'client_trip_history',
				},
			],
		},
	],
};
